import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Grid,
  MenuItem,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  AutoFixHigh as GenerateIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasMinRole } from '../../utils/roles';

/**
 * Vehicle Inspections Management Component
 * Displays list of inspections with filtering and search
 */
const VehicleInspections = ({ onNavigateToNew, onNavigateToInspection }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Inspections are compliance records: only admins may delete them.
  const canDelete = hasMinRole(user, 'admin');
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailDialog, setEmailDialog] = useState({ open: false, inspectionId: null });
  const [emailData, setEmailData] = useState({ email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pdfStatus, setPdfStatus] = useState({ checking: false, available: false });
  const [generatingInspectionId, setGeneratingInspectionId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, inspectionId: null, vehicleName: '' });
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState({
    vehicle_name: '',
    from_date: '',
    to_date: '',
    has_major_defect: '',
    has_minor_defect: '',
    user_id: ''
  });

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await makeApiCall(`api/inspections?${queryParams.toString()}`);
      
      if (response.ok) {
        const data = await response.json();
        setInspections(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchInspections();
  };

  const handleClearFilters = () => {
    setFilters({
      vehicle_name: '',
      from_date: '',
      to_date: '',
      has_major_defect: '',
      has_minor_defect: '',
      user_id: ''
    });
  };

  const handleViewInspection = (inspectionId) => {
    if (onNavigateToInspection) {
      onNavigateToInspection(inspectionId);
    }
  };

  const handleNewInspection = () => {
    if (onNavigateToNew) {
      onNavigateToNew();
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollPdfJobUntilComplete = async (jobId) => {
    const maxAttempts = 120;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await sleep(2000);
      const statusResponse = await makeApiCall(`api/inspections/pdf-jobs/${jobId}`);
      if (!statusResponse.ok) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      const statusData = await statusResponse.json();
      if (statusData.status === 'completed') {
        return statusData.result;
      }
      if (statusData.status === 'failed') {
        throw new Error(statusData.error || t('inspections.messages.failedToGeneratePdf'));
      }
    }
    throw new Error(t('inspections.messages.pdfGenerationTimedOut'));
  };

  const handleGeneratePdf = async (inspectionId, event) => {
    event.stopPropagation();
    try {
      setGeneratingInspectionId(inspectionId);
      const response = await makeApiCall(`api/inspections/${inspectionId}/generate-pdf`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      const data = await response.json();
      if (!data.jobId) {
        throw new Error(t('inspections.messages.failedToGeneratePdf'));
      }

      await pollPdfJobUntilComplete(data.jobId);
      setInspections((prev) =>
        prev.map((inspection) =>
          inspection.id === inspectionId
            ? { ...inspection, has_pdf_report: true }
            : inspection
        )
      );
      alert(t('inspections.messages.pdfGenerated'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(error.message || t('inspections.messages.failedToGeneratePdf'));
    } finally {
      setGeneratingInspectionId(null);
    }
  };

  const handleDownloadPdf = async (inspectionId, event) => {
    event.stopPropagation();
    try {
      const response = await makeApiCall(`api/inspections/${inspectionId}/pdf-download`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'PDF_NOT_GENERATED') {
          alert(t('inspections.messages.pdfRequired'));
          return;
        }
        throw new Error(t('inspections.messages.failedToDownloadPdf'));
      }

      const data = await response.json();

      // Download from S3 signed URL
      const a = document.createElement('a');
      a.href = data.signed_url;
      a.download = data.filename || `inspection-${inspectionId}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      a.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(error.message || t('inspections.messages.failedToDownloadPdf'));
    }
  };

  const handleEmailInspection = async (inspectionId, event) => {
    event.stopPropagation();
    setEmailDialog({ open: true, inspectionId });
    setEmailData({ 
      email: '', 
      subject: t('inspections.messages.emailReportSubject', { id: inspectionId }), 
      message: t('inspections.messages.emailReportMessage')
    });
    setPdfStatus({ checking: true, available: false });
    try {
      const response = await makeApiCall(`api/inspections/${inspectionId}/pdf-status`);
      if (response.ok) {
        const data = await response.json();
        setPdfStatus({ checking: false, available: data.available });
      } else {
        setPdfStatus({ checking: false, available: false });
      }
    } catch {
      setPdfStatus({ checking: false, available: false });
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.email) {
      alert(t('inspections.messages.pleaseEnterEmail'));
      return;
    }

    try {
      setSendingEmail(true);
      const response = await makeApiCall(`api/inspections/${emailDialog.inspectionId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        alert(t('inspections.messages.inspectionReportSent'));
        setEmailDialog({ open: false, inspectionId: null });
        setEmailData({ email: '', subject: '', message: '' });
      } else {
        const errorData = await response.json();
        if (errorData.error === 'EMAIL_AUTH_FAILED') {
          alert(t('inspections.messages.emailAuthFailed'));
        } else {
          alert(errorData.error || t('inspections.messages.failedToSendEmail'));
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(t('inspections.messages.failedToSendEmail'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialog({ open: false, inspectionId: null });
    setEmailData({ email: '', subject: '', message: '' });
  };

  const handleDeleteInspection = (inspection, event) => {
    event.stopPropagation();
    setDeleteDialog({ 
      open: true, 
      inspectionId: inspection.id,
      vehicleName: inspection.vehicle_name
    });
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const response = await makeApiCall(`api/inspections/${deleteDialog.inspectionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Remove from local state
        setInspections(prev => prev.filter(i => i.id !== deleteDialog.inspectionId));
        setDeleteDialog({ open: false, inspectionId: null, vehicleName: '' });
        alert(t('inspections.messages.inspectionDeleted'));
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('inspections.messages.failedToDeleteInspection'));
      }
    } catch (error) {
      console.error('Error deleting inspection:', error);
      alert(t('inspections.messages.failedToDeleteInspection'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, inspectionId: null, vehicleName: '' });
  };

  const getDefectChip = (inspection) => {
    if (inspection.has_major_defect) {
      return <Chip label={t('inspections.statusLabels.majorDefect')} color="error" size="small" />;
    }
    if (inspection.has_minor_defect) {
      return <Chip label={t('inspections.statusLabels.minorDefect')} color="warning" size="small" />;
    }
    if (inspection.has_no_defects) {
      return <Chip label={t('inspections.statusLabels.noDefects')} color="success" size="small" />;
    }
    return <Chip label={t('inspections.statusLabels.unknown')} size="small" />;
  };

  const getTripTypeLabel = (tripTypeId) => {
    const types = {
      1: t('inspections.tripTypes.preTrip'),
      2: t('inspections.tripTypes.postTrip')
    };
    return types[tripTypeId] || t('inspections.statusLabels.na');
  };

  return (
    <Box sx={{ p: 3 }} data-demo-target="page-inspections-root">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {t('inspections.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewInspection}
            data-demo-target="page-inspections-new"
          >
            {t('inspections.newInspection')}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterIcon sx={{ mr: 1 }} />
              {t('inspections.filtersTitle')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label={t('inspections.vehicleName')}
                  value={filters.vehicle_name}
                  onChange={(e) => handleFilterChange('vehicle_name', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label={t('inspections.fromDate')}
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => handleFilterChange('from_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label={t('inspections.toDate')}
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => handleFilterChange('to_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  select
                  label={t('inspections.majorDefects')}
                  value={filters.has_major_defect}
                  onChange={(e) => handleFilterChange('has_major_defect', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">{t('inspections.filters.all')}</MenuItem>
                  <MenuItem value="true">{t('inspections.filters.hasMajorDefects')}</MenuItem>
                  <MenuItem value="false">{t('inspections.filters.noMajorDefects')}</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                  >
                    {t('common.search')}
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    {t('inspections.clearFilters')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Inspections Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inspections.date')}</TableCell>
                      <TableCell>{t('inspections.vehicle')}</TableCell>
                      <TableCell>{t('inspections.plateNumber')}</TableCell>
                      <TableCell>{t('inspections.driver')}</TableCell>
                      <TableCell>{t('inspections.type')}</TableCell>
                      <TableCell>{t('inspections.status')}</TableCell>
                      <TableCell>{t('inspections.trailer')}</TableCell>
                      <TableCell align="center">{t('inspections.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inspections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                            {t('inspections.messages.noInspectionsFound')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      inspections.map((inspection) => (
                        <TableRow key={inspection.id} hover>
                          <TableCell>
                            {format(new Date(inspection.created_at), 'MMM dd, yyyy h:mm a')}
                          </TableCell>
                          <TableCell>{inspection.vehicle_name}</TableCell>
                          <TableCell>{inspection.plate_no}</TableCell>
                          <TableCell>{inspection.driver || inspection.user_name}</TableCell>
                          <TableCell>{getTripTypeLabel(inspection.trip_type_id)}</TableCell>
                          <TableCell>{getDefectChip(inspection)}</TableCell>
                          <TableCell>
                            {inspection.is_trailer_attached ? (
                              <Chip label={inspection.trailer_name} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                {t('inspections.none')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t('inspections.tooltips.viewDetails')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewInspection(inspection.id)}
                                aria-label={t('inspections.tooltips.viewDetails')}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('inspections.tooltips.generatePdf')}>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={(e) => handleGeneratePdf(inspection.id, e)}
                                disabled={generatingInspectionId === inspection.id}
                                aria-label={t('inspections.tooltips.generatePdf')}
                              >
                                {generatingInspectionId === inspection.id
                                  ? <CircularProgress size={18} color="inherit" />
                                  : <GenerateIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('inspections.tooltips.downloadPdf')}>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => handleDownloadPdf(inspection.id, e)}
                                disabled={!inspection.has_pdf_report}
                                aria-label={t('inspections.tooltips.downloadPdf')}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('inspections.tooltips.emailReport')}>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => handleEmailInspection(inspection.id, e)}
                                disabled={!inspection.has_pdf_report}
                                aria-label={t('inspections.tooltips.emailReport')}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title={t('inspections.tooltips.deleteInspection')}>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={(e) => handleDeleteInspection(inspection, e)}
                                  aria-label={t('inspections.tooltips.deleteInspection')}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Email Dialog */}
        <Dialog open={emailDialog.open} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('inspections.emailInspectionReport')}</DialogTitle>
          <DialogContent>
            {pdfStatus.checking ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : !pdfStatus.available ? (
              <Alert severity="warning" sx={{ mt: 1 }} aria-label={t('inspections.messages.pdfRequiredAlert')}>
                {t('inspections.messages.pdfRequired')}
              </Alert>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('inspections.emailAddress')}
                    type="email"
                    value={emailData.email}
                    onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                    placeholder={t('inspections.emailPlaceholder')}
                    aria-label={t('inspections.emailAddress')}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('inspections.subject')}
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    aria-label={t('inspections.subject')}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t('inspections.message')}
                    value={emailData.message}
                    onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                    placeholder={t('inspections.messagePlaceholder')}
                    aria-label={t('inspections.message')}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
              {t('common.cancel')}
            </Button>
            {pdfStatus.available && (
              <Button 
                onClick={handleSendEmail} 
                variant="contained" 
                disabled={sendingEmail || !emailData.email}
                startIcon={sendingEmail ? <CircularProgress size={20} /> : <EmailIcon />}
                aria-label={t('inspections.sendEmail')}
              >
                {sendingEmail ? t('inspections.sending') : t('inspections.sendEmail')}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} maxWidth="sm">
          <DialogTitle>{t('inspections.messages.deleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('inspections.messages.deleteConfirm', { vehicleName: deleteDialog.vehicleName })}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {t('inspections.messages.cannotBeUndone')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={confirmDelete} 
              variant="contained" 
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {deleting ? t('inspections.messages.deleting') : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default VehicleInspections;
